import { create } from 'zustand';
import { api } from '../services/api';

// A registered model as returned by GET /api/config/models.
export interface TestBenchModel {
  id?: string;
  displayName: string;
  provider: string;
  modelId: string;
  capabilities?: Record<string, boolean>;
}

// A content type = the real pipeline field to compare, mapped to its stage.
export interface ContentType {
  key: string;
  label: string;
  stageId: string;
  fields: string[];
  wordOnly?: boolean;
  prereqStage?: string | null;
}

export interface FlaggedContentType {
  key: string;
  label: string;
  stageId: string;
  reason: string;
}

// A real Word Content record (binder-list shape) selectable as a bench input.
export interface BenchWord {
  id: string;
  word: string;
  [key: string]: unknown;
}

// One stored run: a model × word invocation of a real stage prompt.
export interface TestBenchResult {
  id: string;
  contentType: string;
  stageId: string;
  model: string;
  provider: string;
  modelId: string;
  word: string;
  output: string | null;
  surfaced?: Record<string, unknown> | null;
  usage?: Record<string, unknown> | null;
  ok: boolean;
  error?: string | null;
  // True for calls never issued (operator Stop, or provider disabled after an
  // auth/billing error) — distinct from a failed call.
  cancelled?: boolean;
  ts: string;
  // For chained content types: the model that generated the Stage 1 prerequisite.
  prereqModel?: string | null;
}

export interface TestBenchSummary {
  model: string;
  provider: string;
  runs: number;
  lastTs: string | null;
  okRate: number;
}

// Mode B — one result panel within a blind A/B pair.
export interface PromptPanel extends TestBenchResult {
  pairId: string;
  promptVersion: 'A' | 'B';
  promptLabel: string;
  winner?: 'A' | 'B' | null;
}

export interface PromptPair {
  pairId: string;
  word: string;
  panels: PromptPanel[]; // display order is already blind-shuffled by the server
}

export interface StageSourcePrompt {
  contentType: string;
  stageId: string;
  system: string;
  user: string;
}

// 202 response from POST /api/testbench/run and /run-prompt — the run executes
// in the background; the UI polls /api/testbench/run-status for progress.
interface RunStartResponse {
  runId: string;
  total: number;
}

// Progress doc persisted in KV by the worker, one per run.
export interface RunProgress {
  runId: string;
  mode: 'model' | 'prompt';
  status: 'running' | 'complete' | 'error' | 'cancelled';
  total: number;
  done: number;
  current: string | null;
  records?: TestBenchResult[];
  pairs?: PromptPair[];
  error?: string;
}

const POLL_MS = 2000;

// Hard wall-clock cap: if the worker never finalizes (stuck 'running'), stop
// polling after this long and surface a timeout rather than looping forever.
// (L22 fix, 2026-07-05.)
const POLL_MAX_MS = 10 * 60 * 1000; // 10 minutes

// Run ids the operator has stopped — checked by the polling loops so an
// in-flight await exits promptly without waiting for the server to confirm.
const stopFlags = new Set<string>();

// Poll the run-status endpoint until the run completes, errors, is cancelled
// server-side, or the operator hits Stop (shouldStop). onTick fires on every
// successful poll so callers can stream partials in.
async function pollRun(
  runId: string,
  onTick: (p: RunProgress) => void,
  shouldStop?: () => boolean
): Promise<RunProgress | null> {
  // Tolerate a few transient poll failures (KV read blips) before giving up.
  let misses = 0;
  const startedAt = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (shouldStop?.()) return null;
    // Wall-clock cap: bail if the run never finalizes (L22 fix).
    if (Date.now() - startedAt > POLL_MAX_MS) {
      throw new Error('Bench run timed out. It may still be finishing on the server — refresh results in a moment.');
    }
    let p: RunProgress | null = null;
    try {
      p = (await api.get(`/api/testbench/run-status?id=${encodeURIComponent(runId)}`)) as RunProgress;
    } catch {
      misses += 1;
      if (misses > 5) throw new Error('Lost contact with the bench run. It may still be finishing — refresh results in a moment.');
      continue;
    }
    misses = 0;
    onTick(p);
    if (p.status === 'complete' || p.status === 'cancelled') return p;
    if (p.status === 'error') throw new Error(p.error || 'Bench run failed.');
  }
}

interface TestBenchState {
  contentTypes: ContentType[];
  flaggedContentTypes: FlaggedContentType[];
  availableWords: BenchWord[];
  results: TestBenchResult[];
  summary: TestBenchSummary[];
  total: number;
  models: TestBenchModel[];

  loadingContentTypes: boolean;
  loadingWords: boolean;
  loadingResults: boolean;
  loadingModels: boolean;
  running: boolean;
  error: string | null;

  // Mode A staged words — free-typed (any word, real or invented). Shared so the
  // Word Binder's "Add to Model Test" can push a word in.
  stagedWords: string[];

  // Live progress for the active Mode A / Mode B run (null when idle).
  runProgress: RunProgress | null;
  promptRunProgress: RunProgress | null;

  // Mode B state
  promptPairs: PromptPair[];
  runningPrompt: boolean;

  // Word ids/labels that could not be loaded for the last prompt-bench run, so
  // the UI can show that the run was silently shrunk (M14 fix, 2026-07-05).
  skippedWords: string[];

  addStagedWord: (word: string) => void;
  removeStagedWord: (word: string) => void;
  clearStagedWords: () => void;

  // Stop the in-flight run (Mode A / Mode B). Cancels server-side, halts the
  // polling loop, keeps every result already returned.
  stopRun: () => Promise<void>;
  stopPromptRun: () => Promise<void>;

  fetchContentTypes: () => Promise<void>;
  fetchAvailableWords: () => Promise<void>;
  fetchModels: () => Promise<void>;
  fetchResults: (contentType?: string) => Promise<void>;
  runBench: (contentType: string, modelIds: string[], words: string[]) => Promise<void>;
  // Mode B
  fetchStagePrompt: (contentType: string) => Promise<StageSourcePrompt | null>;
  runPromptBench: (args: {
    contentType: string;
    modelId: string;
    wordIds: string[];
    versionA: { label: string; system: string };
    versionB: { label: string; system: string };
  }) => Promise<void>;
  pickWinner: (pairId: string, winner: 'A' | 'B') => Promise<void>;
  clearError: () => void;
}

export const useTestBenchStore = create<TestBenchState>()((set, get) => ({
  contentTypes: [],
  flaggedContentTypes: [],
  availableWords: [],
  results: [],
  summary: [],
  total: 0,
  models: [],

  loadingContentTypes: false,
  loadingWords: false,
  loadingResults: false,
  loadingModels: false,
  running: false,
  error: null,

  stagedWords: [],
  runProgress: null,
  promptRunProgress: null,
  promptPairs: [],
  runningPrompt: false,
  skippedWords: [],

  clearError: () => set({ error: null }),

  addStagedWord: (word: string) => {
    const w = word.trim();
    if (!w) return;
    set((s) => (s.stagedWords.some((x) => x.toLowerCase() === w.toLowerCase())
      ? s
      : { stagedWords: [...s.stagedWords, w] }));
  },
  removeStagedWord: (word: string) => {
    set((s) => ({ stagedWords: s.stagedWords.filter((x) => x !== word) }));
  },
  clearStagedWords: () => set({ stagedWords: [] }),

  stopRun: async () => {
    const runId = get().runProgress?.runId;
    // Only flag a real run id — never add a '' sentinel that can never be
    // cleaned (L22 fix). The polling loop's shouldStop closes over start.runId,
    // so a real id here is what it checks.
    if (runId) {
      stopFlags.add(runId);
      api.post('/api/testbench/cancel', { id: runId }).catch(() => {});
    }
    set({ running: false, runProgress: null });
  },
  stopPromptRun: async () => {
    const runId = get().promptRunProgress?.runId;
    if (runId) {
      stopFlags.add(runId);
      api.post('/api/testbench/cancel', { id: runId }).catch(() => {});
    }
    set({ runningPrompt: false, promptRunProgress: null });
  },

  fetchContentTypes: async () => {
    set({ loadingContentTypes: true });
    try {
      const data = (await api.get('/api/testbench/content-types')) as {
        contentTypes?: ContentType[];
        flagged?: FlaggedContentType[];
      };
      set({
        contentTypes: data.contentTypes || [],
        flaggedContentTypes: data.flagged || [],
        loadingContentTypes: false,
      });
    } catch (err) {
      set({ error: (err as Error).message, loadingContentTypes: false });
    }
  },

  fetchAvailableWords: async () => {
    set({ loadingWords: true });
    try {
      const data = (await api.get('/api/words')) as { words?: BenchWord[] };
      const words = (data.words || [])
        .filter((w) => w && w.id && w.word)
        .sort((a, b) => String(a.word).localeCompare(String(b.word)));
      set({ availableWords: words, loadingWords: false });
    } catch (err) {
      set({ error: (err as Error).message, loadingWords: false });
    }
  },

  fetchModels: async () => {
    set({ loadingModels: true });
    try {
      const data = (await api.get('/api/config/models')) as { models?: TestBenchModel[] };
      set({ models: data.models || [], loadingModels: false });
    } catch (err) {
      set({ error: (err as Error).message, loadingModels: false });
    }
  },

  fetchResults: async (contentType?: string) => {
    set({ loadingResults: true, error: null });
    try {
      const q = contentType ? `?contentType=${encodeURIComponent(contentType)}` : '';
      const data = (await api.get(`/api/testbench/results${q}`)) as {
        results?: TestBenchResult[];
        summary?: TestBenchSummary[];
        total?: number;
      };
      set({
        results: data.results || [],
        summary: data.summary || [],
        total: data.total || 0,
        loadingResults: false,
      });
    } catch (err) {
      set({ error: (err as Error).message, loadingResults: false });
    }
  },

  // Run on bare word strings. The stage is fed only the word; the backend
  // generates any prerequisite (Stage 1) from the word first for chained
  // content types. No existing Airtable record is required — any word works.
  //
  // The worker returns a runId immediately (202) and executes in the
  // background; we poll run-status and stream each finished record into the
  // results grid as it lands. Individual call failures arrive as ok:false
  // records — they show in place without killing the rest of the run.
  runBench: async (contentType: string, modelIds: string[], words: string[]) => {
    set({ running: true, error: null, runProgress: null });
    try {
      const clean = words.map((w) => w.trim()).filter(Boolean);
      if (clean.length === 0) throw new Error('Add at least one word.');
      const start = (await api.post('/api/testbench/run', {
        contentType,
        models: modelIds,
        words: clean,
      })) as RunStartResponse;
      set({ runProgress: { runId: start.runId, mode: 'model', status: 'running', total: start.total, done: 0, current: null } });

      let final: RunProgress | null;
      try {
        final = await pollRun(start.runId, (p) => {
          set({ runProgress: p });
          // Stream finished records into the grid incrementally (newest first,
          // matching fetchResults ordering), deduped by record id.
          const incoming = p.records || [];
          if (incoming.length > 0) {
            set((s) => {
              const have = new Set(s.results.map((r) => r.id));
              const fresh = incoming.filter((r) => !have.has(r.id));
              return fresh.length > 0
                ? { results: [...fresh.slice().reverse(), ...s.results], total: s.total + fresh.length }
                : s;
            });
          }
        }, () => stopFlags.has(start.runId));
      } finally {
        // Always clean the stop flag, whether pollRun returned or threw (L22).
        stopFlags.delete(start.runId);
      }

      if (final === null || final.status === 'cancelled') {
        // Stopped: keep everything already streamed in; skip the authoritative
        // refresh so the in-place cancelled markers stay visible.
        set({ running: false, runProgress: null, error: final?.error || null });
        return;
      }

      // Final authoritative refresh from the persisted index.
      await get().fetchResults(contentType);
      set({ running: false, runProgress: null });
    } catch (err) {
      set({ error: (err as Error).message, running: false, runProgress: null });
      throw err;
    }
  },

  // ---- Mode B: vary the prompt ----
  fetchStagePrompt: async (contentType: string) => {
    try {
      return (await api.get(
        `/api/testbench/stage-prompt?contentType=${encodeURIComponent(contentType)}`
      )) as StageSourcePrompt;
    } catch (err) {
      set({ error: (err as Error).message });
      return null;
    }
  },

  runPromptBench: async ({ contentType, modelId, wordIds, versionA, versionB }) => {
    set({ runningPrompt: true, error: null, promptPairs: [], promptRunProgress: null, skippedWords: [] });
    try {
      const records: BenchWord[] = [];
      // Collect words that fail to load so the run isn't silently shrunk
      // (5 selected → 2 pairs looking like success). Surfaced via skippedWords
      // for the UI (M14 fix, 2026-07-05).
      const skipped: string[] = [];
      for (const id of wordIds) {
        try {
          const d = (await api.get(`/api/words/${id}`)) as { word?: BenchWord };
          if (d.word && d.word.word) records.push(d.word);
          else skipped.push(id);
        } catch {
          skipped.push(id);
        }
      }
      if (skipped.length > 0) set({ skippedWords: skipped });
      if (records.length === 0) throw new Error('Could not load any of the selected words.');
      const start = (await api.post('/api/testbench/run-prompt', {
        contentType,
        model: modelId,
        words: records,
        versionA,
        versionB,
      })) as RunStartResponse;
      set({ promptRunProgress: { runId: start.runId, mode: 'prompt', status: 'running', total: start.total, done: 0, current: null } });

      let final: RunProgress | null;
      try {
        final = await pollRun(start.runId, (p) => {
          // Stream completed pairs in as they land (dedup by pairId).
          set((s) => {
            const have = new Set(s.promptPairs.map((x) => x.pairId));
            const fresh = (p.pairs || []).filter((x) => !have.has(x.pairId));
            return {
              promptRunProgress: p,
              promptPairs: fresh.length > 0 ? [...s.promptPairs, ...fresh] : s.promptPairs,
            };
          });
        }, () => stopFlags.has(start.runId));
      } finally {
        // Always clean the stop flag, whether pollRun returned or threw (L22).
        stopFlags.delete(start.runId);
      }

      if (final === null || final.status === 'cancelled') {
        set({ runningPrompt: false, promptRunProgress: null, error: final?.error || null });
        return;
      }

      set({ promptPairs: final.pairs || get().promptPairs, runningPrompt: false, promptRunProgress: null });
    } catch (err) {
      set({ error: (err as Error).message, runningPrompt: false, promptRunProgress: null });
      throw err;
    }
  },

  pickWinner: async (pairId: string, winner: 'A' | 'B') => {
    // Snapshot the target pair's panels so we can roll back the optimistic
    // reveal if the API call fails (M14 fix, 2026-07-05).
    const prevPanels = get().promptPairs.find((p) => p.pairId === pairId)?.panels;
    // Optimistically mark the pair's panels with the winner + reveal labels.
    set((s) => ({
      promptPairs: s.promptPairs.map((p) =>
        p.pairId === pairId
          ? { ...p, panels: p.panels.map((pn) => ({ ...pn, winner })) }
          : p
      ),
    }));
    try {
      await api.post('/api/testbench/pick-winner', { pairId, winner });
    } catch (err) {
      // Roll back the optimistic reveal and surface the failure.
      if (prevPanels) {
        set((s) => ({
          promptPairs: s.promptPairs.map((p) =>
            p.pairId === pairId ? { ...p, panels: prevPanels } : p
          ),
        }));
      }
      set({ error: (err as Error).message });
    }
  },
}));
